import React, { useState, useCallback } from 'react'
import styles from './CompaniesSection.module.css'
import { companies, getAllCategories } from '../../../data/companies'
import { useScrollProgress } from '../../../hooks/useParallax'
import { useScrollAnimation } from '../../../hooks/useScrollAnimation'

// 3D Tilt Card Component
const TiltCard = ({ company, index, isInView }) => {
  const [tilt, setTilt] = useState({ x: 0, y: 0, isHovering: false })

  const handleMouseMove = useCallback((e) => {
    const rect = e.currentTarget.getBoundingClientRect()
    const x = (e.clientX - rect.left) / rect.width
    const y = (e.clientY - rect.top) / rect.height

    const tiltX = (y - 0.5) * 10
    const tiltY = (x - 0.5) * -10

    setTilt({ x: tiltX, y: tiltY, isHovering: true })
  }, [])

  const handleMouseLeave = useCallback(() => {
    setTilt({ x: 0, y: 0, isHovering: false })
  }, [])

  return (
    <div
      className={styles.projectCard}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{
        opacity: isInView ? 1 : 0,
        transform: isInView
          ? tilt.isHovering
            ? `perspective(1000px) rotateX(${tilt.x}deg) rotateY(${tilt.y}deg) scale(1.02)`
            : 'perspective(1000px) rotateX(0deg) rotateY(0deg) scale(1)'
          : 'translateY(40px)',
        transition: tilt.isHovering
          ? 'transform 0.1s ease-out, opacity 0.6s ease'
          : `transform 0.5s ease-out, opacity 0.6s ease ${index * 0.1}s`
      }}
    >
      {/* Shine effect on hover */}
      <div
        className={styles.cardShine}
        style={{
          opacity: tilt.isHovering ? 0.15 : 0,
          background: `radial-gradient(circle at ${(tilt.y + 5) * 10}% ${(tilt.x + 5) * 10}%, rgba(255,255,255,0.8) 0%, transparent 60%)`
        }}
      />

      <div className={styles.thumbnailWrapper}>
        <img
          src={company.thumbnail}
          alt={company.name}
          className={styles.thumbnail}
          loading="lazy"
        />
      </div>

      <div className={styles.cardContent}>
        <div className={styles.technologies}>
          {company.technologies.map((tech, techIndex) => (
            <span key={techIndex} className={styles.techTag}>{tech}</span>
          ))}
        </div>

        <h3 className={styles.projectName}>{company.name}</h3>
        <p className={styles.description}>{company.description}</p>

        <div className={styles.cardFooter}>
          <span className={styles.metric}>{company.metric}</span>
        </div>
      </div>
    </div>
  )
}

const CompaniesSection = () => {
  const [selectedCategory, setSelectedCategory] = useState('All')
  const categories = ['All', ...getAllCategories()]
  const { ref: sectionRef, isInView } = useScrollProgress()
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation({ threshold: 0.3 })

  const filteredCompanies = selectedCategory === 'All'
    ? companies
    : companies.filter(company => company.category === selectedCategory)

  return (
    <section id="projects" className={styles.companies} ref={sectionRef}>
      {/* Decorative background elements */}
      <div className={styles.bgDecoration}>
        <div className={styles.bgCircle1} style={{ opacity: isInView ? 0.5 : 0 }} />
        <div className={styles.bgCircle2} style={{ opacity: isInView ? 0.3 : 0 }} />
      </div>

      <div className={styles.container}>
        <div
          ref={headerRef}
          className={styles.header}
          style={{
            opacity: headerVisible ? 1 : 0,
            transform: headerVisible ? 'translateY(0)' : 'translateY(30px)',
            transition: 'all 0.8s cubic-bezier(0.16, 1, 0.3, 1)'
          }}
        >
          <h2 className={styles.sectionTitle}>Featured Projects</h2>
          <p className={styles.sectionDescription}>
            A selection of case studies in architecture and automation.
          </p>
          <div className={styles.titleUnderline}></div>
        </div>

        <div
          className={styles.filters}
          style={{
            opacity: isInView ? 1 : 0,
            transform: isInView ? 'translateY(0)' : 'translateY(20px)',
            transition: 'all 0.6s cubic-bezier(0.16, 1, 0.3, 1) 0.1s'
          }}
        >
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`${styles.filterBtn} ${selectedCategory === category ? styles.active : ''}`}
            >
              {category}
            </button>
          ))}
        </div>

        <div className={styles.gallery}>
          {filteredCompanies.map((company, index) => (
            <TiltCard
              key={company.id}
              company={company}
              index={index}
              isInView={isInView}
            />
          ))}
        </div>
      </div>
    </section>
  )
}

export default CompaniesSection
